import React, { useCallback, useMemo } from 'react'
import clsx from 'clsx'

import { ReactComponent as ClassicListeningSVG } from 'resources/graphics/astrid_classic_listen.svg'
import { ReactComponent as ClassicReadingSVG } from 'resources/graphics/astrid_classic_read.svg'
import { ReactComponent as WaterbearListenSVG } from 'resources/graphics/waterbear_listen.svg'
import { ReactComponent as WaterbearReadSVG } from 'resources/graphics/waterbear_read.svg'
import { ReactComponent as ListeningIconSVG } from 'resources/graphics/listening_icon.svg'
import { ReactComponent as ReadingIconSVG } from 'resources/graphics/reading_icon.svg'
import { BookBranding, BookMode, BookType, BookWithProgress } from 'store/services/Book'
import { LearningJourneyEntryAttributes } from 'store/services/Journey'
import { getActivityLabelFromAttributes } from 'store/services/Library'

import { Label, LabelType } from '../Label'
import styles from './BookCard.module.scss'

type BookCardProps = {
  book?: BookWithProgress | null
  attributes: LearningJourneyEntryAttributes
  isLocked?: boolean
  isCarousel?: boolean
  isRecommended?: boolean
  isPlacementTest?: boolean
  bookMode?: BookMode
  className?: string
  tabIndex?: number
  itemRef?: (element: HTMLElement | null) => HTMLElement | null
  onClick: (book?: BookWithProgress | null) => void
  onFocus?: () => void
}

const RightIcon = React.memo(
  ({ branding, bookMode, bookType }: { branding: BookBranding; bookMode: BookMode; bookType: BookType }) => {
    const isListening = bookType === BookType.Listening || (bookType === BookType.Mix && bookMode === BookMode.LISTEN)

    switch (branding) {
      case BookBranding.ASTRID_CLASSIC:
        return isListening ? (
          <ClassicListeningSVG className={styles.astridIcon} />
        ) : (
          <ClassicReadingSVG className={styles.astridIcon} />
        )

      case BookBranding.WATERBEAR:
        return isListening ? (
          <WaterbearListenSVG className={styles.waterbearIcon} />
        ) : (
          <WaterbearReadSVG className={styles.waterbearIcon} />
        )

      default:
        return isListening ? (
          <ListeningIconSVG className={styles.typeIcon} />
        ) : (
          <ReadingIconSVG className={styles.typeIcon} />
        )
    }
  }
)

export const BookCard = ({
  book,
  attributes,
  isLocked,
  isRecommended,
  isPlacementTest,
  bookMode = BookMode.READ,
  className,
  onClick,
  itemRef,
  tabIndex,
  onFocus
}: BookCardProps) => {
  const progress = useMemo<number | null>(() => {
    if (!book || !book.bookProgress) return null

    const { index, indexListening, isCompleted, isCompletedListening } = book.bookProgress

    const total = book.book.exercises.length

    if (bookMode === BookMode.READ) {
      if (isCompleted) return 100
      return index > 0 ? (index / total) * 100 : null
    }

    if (bookMode === BookMode.LISTEN) {
      if (isCompletedListening) return 100
      return indexListening > 0 ? (indexListening / total) * 100 : null
    }

    return null
  }, [book, bookMode])

  const label = useMemo(
    () =>
      getActivityLabelFromAttributes(attributes, {
        locked: isLocked,
        withoutText: !isRecommended,
        large: true
      }),
    [attributes, isLocked, isRecommended]
  )

  const handleClick = useCallback(() => {
    onClick(book)
  }, [book, onClick])

  const bgImage = isPlacementTest ? './placement.png' : book?.book.imageUrl

  return (
    <button
      ref={itemRef}
      tabIndex={tabIndex}
      onFocus={onFocus}
      type="button"
      className={clsx(styles.card, className)}
      onClick={handleClick}
      aria-label={`Open book ${book?.book.title}`}
    >
      <div className={styles.wrapper} style={{ backgroundImage: `url(${bgImage})` }}>
        {!isPlacementTest && <div className={styles.gradient} />}
        {isRecommended && <div className={styles.shiningAnimation} />}
        <div className={styles.content}>
          <div className={styles.head}>
            <div className={styles.headLeft}>
              {label && <Label {...label} />}
              <Label
                type={LabelType.STARS}
                large
                title={isPlacementTest ? '1000' : book?.book.totalPoints.toString()}
              />
            </div>
            {book && !isPlacementTest && !isRecommended && (
              <RightIcon branding={book.book.branding} bookType={book.book.bookType} bookMode={bookMode} />
            )}
          </div>
          <div className={styles.footer}>
            {(!book?.book.hideTitle || isPlacementTest) && (
              <div className={styles.title}>{isPlacementTest ? 'Find your English level' : book?.book.title}</div>
            )}
            {!isPlacementTest && !isRecommended && progress !== null && (
              <div
                className={styles.progressBar}
                role="progressbar"
                aria-label={`Book progress`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(progress)}
              >
                <div className={styles.progressFill} style={{ width: `${progress}%` }} aria-hidden="true" />
                <div className={styles.progressBorder} aria-hidden="true" />
              </div>
            )}
          </div>
        </div>
      </div>
    </button>
  )
}
